import { useMemo } from 'react';
import { useEpisodeTraceability } from './useEpisodeTraceability';
import { useEpisodeContextual } from './useEpisodeContextual';
import { useTranscript } from './useTranscript';
import { ITranscript } from '@insights_module/types/Episode';
import { useEpisodeDetailsContext } from '@insights_module/context/EpisodeDetailsContext';
import {
  aggregateSegmentIdsByContextualCategory,
  aggregateSegmentIdsByGarm,
  getParagraphsDataForSegment,
} from '@insights_module/utils/episodesUtils';
import { CategoryType, GarmRiskLevelType } from '@insights_module/types/BrandSafety';

export const useEpisodeSegment = () => {
  const {
    selectedSegment: selectedSegmentId,
    selectedGarmCategory,
    selectedGarmLevel,
    selectedContextualCategory,
  } = useEpisodeDetailsContext();

  const { episodeSegments } = useEpisodeTraceability();
  const { contextualData } = useEpisodeContextual();
  const { getPartialTranscript, transcript } = useTranscript();

  const selectedSegment = useMemo(() => {
    if (!episodeSegments.length || selectedSegmentId === null) return undefined;

    return episodeSegments[selectedSegmentId];
  }, [episodeSegments, selectedSegmentId]);

  const selectedSegmentTranscript = useMemo(() => {
    if (!selectedSegment) return [] as ITranscript[];

    getPartialTranscript(selectedSegment.startTime, selectedSegment.endTime);
  }, [selectedSegment, getPartialTranscript]);

  const segmentsByGarm = useMemo(() => {
    return aggregateSegmentIdsByGarm(episodeSegments);
  }, [episodeSegments]);

  // filtered segments by garm category and risk level by selection from sidebar
  const segmentIdsFilteredByGarm = useMemo(() => {
    if (selectedGarmCategory && selectedGarmLevel) {
      return (
        segmentsByGarm[selectedGarmCategory as CategoryType]?.[
          selectedGarmLevel as GarmRiskLevelType
        ] || []
      ).map((segment) => segment.id) as number[];
    }

    if (selectedGarmCategory) {
      const ids = segmentsByGarm[selectedGarmCategory as CategoryType];
      return Array.from(new Set(Object.values(ids).flat()))
        .sort((a, b) => a.start - b.start)
        .map((segment) => segment.id) as number[];
    }

    return [];
  }, [segmentsByGarm, selectedGarmCategory, selectedGarmLevel]);

  const segmentsByContextualCategory = useMemo(() => {
    return aggregateSegmentIdsByContextualCategory(
      contextualData.map((category) => category.name),
      episodeSegments
    );
  }, [contextualData, episodeSegments]);

  // filtered segments by contextual category by selection from sidebar
  const segmentIdsFilteredByContextualCategory = useMemo(() => {
    if (selectedContextualCategory) {
      return segmentsByContextualCategory[selectedContextualCategory] || [];
    }

    return [];
  }, [segmentsByContextualCategory, selectedContextualCategory]);

  const segmentsWithParagraphs = useMemo(() => {
    if (!transcript.length) return [];

    return episodeSegments.map((segment) => ({
      ...segment,
      paragraphData: getParagraphsDataForSegment(
        getPartialTranscript(segment.startTime, segment.endTime)
      ),
    }));
  }, [episodeSegments, transcript, getPartialTranscript]);

  return {
    selectedSegment,
    selectedSegmentTranscript,
    episodeSegments,
    transcript,
    segmentsByGarm,
    segmentIdsFilteredByGarm,
    segmentIdsFilteredByContextualCategory,
    segmentsWithParagraphs,
  };
};
